import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNG } from 'primeng/config';
import { primeNgDateTranslation } from '../shared/format/primeng-locale';

export const DEFAULT_LOCALE = 'en';
export const SUPPORTED_LOCALES = ['en', 'ar'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** The language last used by a signed-in user, kept in this browser so the sign-in page opens in it after a logout (issue #106). */
const LAST_LOCALE_KEY = 'fms.lastLocale';

function isSupported(locale: string | null | undefined): locale is string {
  return (SUPPORTED_LOCALES as readonly string[]).includes(locale ?? '');
}

function readLastLocale(): string | null {
  try {
    const stored = localStorage.getItem(LAST_LOCALE_KEY);
    return isSupported(stored) ? stored : null;
  } catch {
    return null; // storage can be unavailable (private window, blocked site data): the default language then applies
  }
}

function rememberLocale(locale: string): void {
  try {
    localStorage.setItem(LAST_LOCALE_KEY, locale);
  } catch {
    // not remembered; nothing else depends on it
  }
}

const RTL_LOCALES: readonly string[] = ['ar'];

/** Applies a locale to both the translation service and the document's lang/dir attributes. */
@Injectable({ providedIn: 'root' })
export class LocaleService {
  private readonly translate = inject(TranslateService);
  private readonly primeng = inject(PrimeNG);

  constructor() {
    // The browser tab is titled with the product name in the current language ("Masharef" / «مشارف»); index.html carries
    // the English name for the moment before the first translations arrive.
    const setTitle = () => {
      const name = this.translate.instant('shared.logoAlt');
      if (typeof name === 'string' && name && name !== 'shared.logoAlt') document.title = name;
    };
    this.translate.onLangChange.subscribe(setTitle);
    this.translate.onTranslationChange.subscribe(setTitle);
  }

  readonly supportedLocales = SUPPORTED_LOCALES;

  /** A signed-in user's preferred language is applied and remembered; with nobody signed in (the sign-in page after a logout) the last remembered language
   * applies, and only a browser that has never had a signed-in user falls back to the default. */
  apply(locale: string | null | undefined): void {
    let resolved: string;
    if (isSupported(locale)) {
      resolved = locale;
      rememberLocale(resolved);
    } else {
      resolved = readLastLocale() ?? DEFAULT_LOCALE;
    }
    this.translate.use(resolved);
    // Every PrimeNG date picker: the locale's month and day names, Sunday first.
    this.primeng.setTranslation(primeNgDateTranslation(resolved));
    document.documentElement.lang = resolved;
    document.documentElement.dir = RTL_LOCALES.includes(resolved) ? 'rtl' : 'ltr';
  }
}
